import React, { useEffect, useState } from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { sendApiRequest } from '../../../api/request';
import './visitorDashboard.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const TIME_RANGES = {
  '1h': 'Last hour',
  '24h': 'Last 24 hours',
  '7d': 'Last week',
  '30d': 'Last month',
};

const VisitorDashboard = () => {
  const [sessions, setSessions] = useState([]);
  const [actions, setActions] = useState([]);
  const [timeRange, setTimeRange] = useState('24h');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sessionData, actionData] = await Promise.all([
          sendApiRequest('GET', '/visitor/session'),
          sendApiRequest('GET', '/visitor/action'),
        ]);
        setSessions(sessionData);
        setActions(actionData);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  const filterByTime = (data, key) => {
    const now = new Date();
    let cutoff;

    switch (timeRange) {
      case '1h':
        cutoff = new Date(now.getTime() - 1 * 60 * 60 * 1000);
        break;
      case '24h':
        cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        cutoff = new Date(0);
    }

    return data.filter((item) => {
      // Treat item[key] as UTC
      const itemTime = new Date(item[key] + 'Z'); // append Z if missing
      return itemTime >= cutoff;
    });
  };

  const filteredSessions = filterByTime(sessions, 'startTime');
  const filteredActions = filterByTime(actions, 'actionTime');

  const totalVisitors = filteredSessions.length;
  const totalActions = filteredActions.length;

  const avgSessionDuration =
    filteredSessions.length > 0
      ? filteredSessions.reduce((sum, s) => sum + (s.durationSeconds || 0), 0) /
      filteredSessions.length
      : 0;

  const avgActions =
    Object.values(
      filteredActions.reduce((acc, action) => {
        acc[action.actionType] = (acc[action.actionType] || 0) + 1;
        return acc;
      }, {})
    ).length > 0
      ? totalActions /
      Object.values(
        filteredActions.reduce((acc, action) => {
          acc[action.actionType] = (acc[action.actionType] || 0) + 1;
          return acc;
        }, {})
      ).length
      : 0;

  // ------------------ Charts ------------------
  const sessionLineChart = {
    labels: filteredSessions.map((s) =>
      new Date(s.startTime).toLocaleString()
    ),
    datasets: [
      {
        label: 'Session Duration (s)',
        data: filteredSessions.map((s) => s.durationSeconds),
        borderColor: 'rgba(75,192,192,1)',
        backgroundColor: 'rgba(75,192,192,0.2)',
        tension: 0.3,
      },
    ],
  };

  const actionBarChart = {
    labels: Object.keys(
      filteredActions.reduce((acc, a) => {
        acc[a.actionType] = true;
        return acc;
      }, {})
    ),
    datasets: [
      {
        label: 'Action Counts',
        data: Object.values(
          filteredActions.reduce((acc, a) => {
            acc[a.actionType] = (acc[a.actionType] || 0) + 1;
            return acc;
          }, {})
        ),
        backgroundColor: 'rgba(153,102,255,0.6)',
      },
    ],
  };

  const sessionPieChart = {
    labels: ['Mobile', 'Desktop'],
    datasets: [
      {
        data: [
          filteredSessions.filter((s) => s.isMobile).length,
          filteredSessions.filter((s) => !s.isMobile).length,
        ],
        backgroundColor: ['rgba(54,162,235,0.6)', 'rgba(255,159,64,0.6)'],
      },
    ],
  };

  const actionPieChart = {
    labels: Object.keys(
      filteredActions.reduce((acc, a) => {
        acc[a.actionType] = true;
        return acc;
      }, {})
    ),
    datasets: [
      {
        data: Object.values(
          filteredActions.reduce((acc, a) => {
            acc[a.actionType] = (acc[a.actionType] || 0) + 1;
            return acc;
          }, {})
        ),
        backgroundColor: [
          'rgba(255,99,132,0.6)',
          'rgba(54,162,235,0.6)',
          'rgba(255,206,86,0.6)',
          'rgba(75,192,192,0.6)',
          'rgba(153,102,255,0.6)',
        ],
      },
    ],
  };

  return (
    <div className="visitor-dashboard">
      <div className="mb-4">
        <label className="time-range-label">Time Range:</label>
        <select
          className="time-range-select"
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
        >
          {Object.entries(TIME_RANGES).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="columns">
        {/* Sessions Column */}
        <div className="column">
          <h2>Visitor Sessions</h2>
          <div className="metrics">
            <p>
              Total Visitors: <span>{totalVisitors}</span>
            </p>
            <p>
              Average Duration: <span>{avgSessionDuration.toFixed(2)}s</span>
            </p>
          </div>
          <div className="chart-container">
            <Line data={sessionLineChart} />
          </div>
          <h3 className="mt-4">Mobile vs Desktop</h3>
          <div className="pie-chart-container">
            <Pie data={sessionPieChart} />
          </div>
        </div>

        {/* Actions Column */}
        <div className="column">
          <h2>Visitor Actions</h2>
          <div className="metrics">
            <p>
              Total Actions: <span>{totalActions}</span>
            </p>
            <p>
              Average per type: <span>{avgActions.toFixed(2)}</span>
            </p>
          </div>
          <div className="chart-container">
            <Bar data={actionBarChart} />
          </div>
          <h3 className="mt-4">Actions by Type</h3>
          <div className="pie-chart-container">
            <Pie data={actionPieChart} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorDashboard;
